import React, { FC, ReactNode, JSX, useCallback, useMemo } from 'react';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons/faExclamationCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useController } from 'react-hook-form';
import classNames from 'classnames';
import Select, { Props, MultiValue, StylesConfig } from 'react-select';

import { generateSelectStyles } from './selectField';

import styles from './fieldStyles.scss';

type Option = { label: string | JSX.Element; value: string };

const MultiSelectField: FC<{
  disabled?: boolean;
  generateHelperElement?: (selectedOptions: Option[]) => ReactNode;
  label?: string;
  name: string;
  onChangeCallback?: (values: string[]) => void;
  options: Option[];
  size?: 'small' | 'medium' | 'large';
} & Pick<Props, 'isClearable' | 'isSearchable' | 'placeholder'>> = ({
  disabled,
  generateHelperElement,
  isClearable,
  isSearchable = false,
  label,
  name,
  onChangeCallback,
  options,
  placeholder,
  size = 'medium'
}) => {
  const {
    field: { onChange, value, ...field },
    fieldState: { error },
    formState: { isSubmitting }
  } = useController({ name });

  const isDisabled = disabled || isSubmitting;

  const selectedOptions = useMemo(() => {
    const values = Array.isArray(value) ? value : [];
    return options.filter(option => values.includes(option.value));
  }, [options, value]);

  const handleOnChange = useCallback(
    (selectedOptions: MultiValue<Option>) => {
      const values = selectedOptions.map(option => option.value);
      onChange(values);
      if (onChangeCallback) onChangeCallback(values);
    },
    [onChange, onChangeCallback]
  );

  const selectStyles = useMemo(
    () => generateSelectStyles({ error: Boolean(error), size }) as StylesConfig<Option, true>,
    [error, size]
  );

  return (
    <div
      className={classNames(styles.field, {
        [styles.small]: size === 'small',
        [styles.medium]: size === 'medium',
        [styles.large]: size === 'large'
      })}
    >
      {label ? (
        <label data-title htmlFor={name}>
          {label}
        </label>
      ) : null}
      <Select
        {...field}
        inputId={name}
        classNamePrefix={name}
        isClearable={isClearable}
        isDisabled={isDisabled}
        isMulti
        isSearchable={isSearchable}
        onChange={handleOnChange}
        options={options}
        placeholder={placeholder}
        styles={selectStyles}
        value={selectedOptions}
      />
      {error?.message ? (
        <label data-error htmlFor={name}>
          <FontAwesomeIcon icon={faExclamationCircle} />
          {error.message}
        </label>
      ) : null}
      {generateHelperElement ? generateHelperElement(selectedOptions) : null}
    </div>
  );
};

export default MultiSelectField;
